import { Dialog as RadixDialog } from 'radix-ui';
import { createContext, useContext, type ComponentPropsWithoutRef } from 'react';
import { Theme } from '@radix-ui/themes';
import { cn } from '@/lib/utils';

export type SheetDirection = 'bottom' | 'top' | 'left' | 'right';

const SheetDirectionContext = createContext<SheetDirection>('bottom');

interface SheetRootProps extends ComponentPropsWithoutRef<typeof RadixDialog.Root> {
  /** Edge the sheet anchors to. */
  direction?: SheetDirection;
}

function SheetRoot({ direction = 'bottom', ...props }: SheetRootProps) {
  return (
    <SheetDirectionContext.Provider value={direction}>
      <RadixDialog.Root {...props} />
    </SheetDirectionContext.Provider>
  );
}

function SheetContent({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof RadixDialog.Content>) {
  const direction = useContext(SheetDirectionContext);
  const handle = <div className="pulp-sheet-handle" aria-hidden />;
  return (
    <RadixDialog.Portal>
      <Theme>
        <RadixDialog.Overlay className="pulp-sheet-overlay" />
        <RadixDialog.Content
          data-direction={direction}
          className={cn('pulp-sheet', direction === 'bottom' && 'pb-safe-area', className)}
          aria-describedby={undefined}
          {...props}
        >
          {direction === 'bottom' && handle}
          {children}
          {direction === 'top' && handle}
        </RadixDialog.Content>
      </Theme>
    </RadixDialog.Portal>
  );
}
SheetContent.displayName = 'Sheet.Content';

export function Sheet(props: SheetRootProps) {
  return <SheetRoot {...props} />;
}

Sheet.Root = SheetRoot;
Sheet.Trigger = RadixDialog.Trigger;
Sheet.Content = SheetContent;
Sheet.Title = RadixDialog.Title;
Sheet.Description = RadixDialog.Description;
Sheet.Close = RadixDialog.Close;
